import { GridColDef } from '@mui/x-data-grid-pro';
import { Policy } from '../../../types';
import { useMemo, useState } from 'react';
import PoliciesGridToolbar from './policies-grid-toolbar';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { useQuery } from '@tanstack/react-query';
import { getAllPoliciesQuery } from '../../api/users-api';
import AvatarChip from '../../shared/avatar-chip';
import DeletePoliciesDialog from './delete-policies-dialog';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../utils/use-user-settings';
import { useSearchParams } from 'react-router-dom';
import { useToolbar } from '../../shared/shared-data-grid-toolbar-helper';
import SharedDataGridClient from '../../shared/shared-data-grid-client';
import { usePolicyActions } from './use-policy-actions';
import PolicyActionsCell from './policy-actions-cell';
import RunPoliciesDialog from './run-policies-dialog';
import PolicyRounded from '@mui/icons-material/PolicyRounded';

interface PoliciesGridProps {
  changeLogRoles: string[];
  toolsRoles: string[];
}

export default function PoliciesGrid({
  changeLogRoles,
  toolsRoles,
}: PoliciesGridProps) {
  const [openDeletePolicies, setOpenDeletePolicies] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState<number[]>([]);
  const [rowRunTarget, setRowRunTarget] = useState<number[]>([]);
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const { handlePolicyEdit, handlePolicyClone } = usePolicyActions();

  const {
    data: policies,
    isLoading,
    isFetching,
  } = useQuery({
    ...getAllPoliciesQuery(),
  });
  const { viewType } = useUserSettings();

  const columns: GridColDef<Policy>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 165,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1,
        renderCell: (params) => {
          if (!params.value) {
            return '-';
          }

          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'policyName',
        headerName: 'Policy Name',
        minWidth: 170,
        flex: 1,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'policyType',
        headerName: 'Policy Type',
        width: 140,
        type: 'singleSelect',
        valueOptions: [
          { label: 'Standard', value: 'standard' },
          { label: 'New User Created', value: 'newUser' },
        ],
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'lastRunDate',
        headerName: 'Last Ran On',
        width: 150,
        type: 'dateTime',
        valueGetter: (value: number | null) => {
          if (!value) {
            return null;
          } else {
            const date = new Date(value);
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (!params) {
            return 'Never';
          }
          return params.toLocaleString();
        },
      },
      {
        field: 'manualRun',
        headerName: 'Manual Run',
        type: 'boolean',
        width: 95,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'scheduled',
        headerName: 'Scheduled',
        type: 'boolean',
        width: 95,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 185,
        sortable: false,
        filterable: false,
        display: 'flex',
        renderCell: (params) => {
          const policy = params.row;
          if (!policy || isLoading || isFetching) return null;
          return (
            <PolicyActionsCell
              policyStatus={policy.status ?? 'IDLE'}
              canRun={isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.MaunalRunPolicy.value
              )}
              isNewUser={policy.policyType === 'newUser'}
              onRun={(e) => {
                e.stopPropagation();
                setRowRunTarget([policy.id]);
                setOpenRunDialog(true);
              }}
              canEdit={isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.EditPolicy.value
              )}
              canClone={isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.CopyPolicy.value
              )}
              canDelete={isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DeletePolicy.value
              )}
              onEdit={() => handlePolicyEdit(policy)}
              onClone={() => handlePolicyClone(policy)}
              onDelete={() => {
                setSelectedPolicies([policy.id]);
                setOpenDeletePolicies(true);
              }}
            />
          );
        },
      },
    ],
    [isFetching, isLoading, toolsRoles]
  );

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdDate',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      toolsRoles,
      setOpenDeletePolicies,
      selectedPolicies,
      policyList: policies || [],
      setOpenRunDialog,
    }),
    [toolsRoles, selectedPolicies, policies]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.policiesGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(PoliciesGridToolbar, toolbarProps),
          ...createGridOverlays('Policies', <PolicyRounded />),
        }}
        rows={policies || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={isLoading || isFetching}
        checkboxSelection
        disableRowSelectionOnClick
        selectedRows={selectedPolicies}
        setSelectedRows={setSelectedPolicies}
        gridStateKey="policiesGridStates"
        defaultGridState={defaultGridState}
        onRowClick={(row) => {
          if (!policies?.find((log) => log.id === row.row.id)) return;
          setSearchParams((prev) => {
            prev.set('selectedPolicy', row.row.id.toString());
            return prev;
          });
        }}
      />

      {openRunDialog ? (
        <RunPoliciesDialog
          open={openRunDialog}
          handleClose={() => {
            setOpenRunDialog(false);
            setRowRunTarget([]);
          }}
          selectedIds={
            rowRunTarget.length > 0 ? rowRunTarget : selectedPolicies
          }
          setSelectedIds={
            rowRunTarget.length > 0 ? setRowRunTarget : setSelectedPolicies
          }
          policyList={policies || []}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      {openDeletePolicies && selectedPolicies.length > 0 ? (
        <DeletePoliciesDialog
          open={openDeletePolicies}
          handleClose={() => {
            setOpenDeletePolicies(false);
          }}
          changeLogRoles={changeLogRoles}
          selectedPolicies={selectedPolicies}
          setSelectedPolicies={setSelectedPolicies}
          policyList={policies || []}
        />
      ) : null}
    </>
  );
}
